import React from "react";
import { BaseEdge, EdgeLabelRenderer, getBezierPath, Position } from "@xyflow/react";
import { ConfigureIcon, NotPossibleIcon } from "../flowIcons";
import { FLOW_COLORS } from "./flowMapTypes";
import { HoverTooltip } from "../../common/HoverTooltip";
import { useTranslation } from "react-i18next";

const stateWord: Record<string, string> = { enabled: "Enabled", disabled: "Disabled", "not-enableable": "Not available" };

const DirectionEdge = ({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, data }: any) => {
    const { t } = useTranslation();
    const state = data?.state || "disabled";
    const [path, labelX, labelY] = getBezierPath({
        sourceX, sourceY, targetX, targetY,
        sourcePosition: sourcePosition || Position.Right,
        targetPosition: targetPosition || Position.Left,
    });
    const stroke = FLOW_COLORS[state as keyof typeof FLOW_COLORS] || FLOW_COLORS.disabled;
    const strokeWidth = state === "enabled" ? 2.5 : 2;
    const strokeDasharray = state === "enabled" ? undefined : "5 4";
    const onClick = () => data?.onSelect?.(data.destinationId, data.direction);
    // The arrow's colour already says what state the direction is in, so the only
    // thing riding on the line is the button that opens its configuration — crossed
    // out when the direction can never be enabled.
    const impossible = state === "not-enableable";
    const flowLabel = data?.direction === "out" ? t("Sends data") : t("Receives data");
    const title = `${flowLabel} · ${t(stateWord[state] as any)} — ${impossible ? t("why not") : t("configure")}`;

    return (
        <>
            {/* fat transparent hit area so the line itself is clickable */}
            <path d={path} fill="none" stroke="transparent" strokeWidth={18}
                style={{ pointerEvents: "stroke", cursor: "pointer" }} onClick={onClick} />
            <BaseEdge id={id} path={path} style={{ stroke, strokeWidth, strokeDasharray }}
                markerEnd={`url(#flow-arrow-${state})`} />
            <EdgeLabelRenderer>
                {/* The transform lives on a wrapper so the tooltip anchor moves with the label. */}
                <div className="absolute pointer-events-auto"
                    style={{ transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)` }}>
                    <HoverTooltip content={data?.needsSetup && !impossible ? t("Configure Flow — setup needed") : t("Configure Flow")}
                        position="top" portal focusable={false}>
                        <button
                            type="button"
                            onClick={onClick}
                            aria-label={title}
                            className={`relative cursor-pointer flex items-center justify-center w-7 h-7 rounded-full border bg-white transition-colors ${
                                // Focus ring matches the direction's state — a green
                                // ring on a disabled flow reads as "this is on".
                                data?.isSelected
                                    ? state === "enabled"
                                        ? "border-route-green ring-2 ring-route-green text-route-green"
                                        : "border-medium-gray ring-2 ring-medium-gray text-off-black"
                                    : state === "enabled"
                                        ? "border-route-green text-route-green hover:bg-light-green"
                                        : "border-gray-lines text-medium-gray hover:text-off-black"}`}>
                            {impossible ? <NotPossibleIcon /> : <ConfigureIcon />}
                            {data?.needsSetup && !impossible && (
                                <span className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 rounded-full bg-orange-50 text-orange border border-orange-200 text-[9px] font-bold flex items-center justify-center">
                                    !
                                </span>
                            )}
                        </button>
                    </HoverTooltip>
                    {/* Field mapping is per flow, so its badge rides on the arrow —
                        above it, away from the second arrow running underneath. */}
                    {data?.hasRules && (
                        <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1 whitespace-nowrap bg-light-green text-route-green rounded-full px-2 py-0.5 text-[11px] font-semibold">
                            {t("Field mapping")}
                        </span>
                    )}
                </div>
            </EdgeLabelRenderer>
        </>
    );
};

export default DirectionEdge;
